/**
 * "Set how it starts", from the Apps page's problem box when the build found no stack ("No supported stack detected"):
 * How it runs (BuilderPicker) in a dialog, in `setup`: the recommended builder preselected when the folder has one
 * (one click: a compose file of images, a Makefile target, …), else Custom command with suggestions from the folder.
 * "Save and start" writes the service's `.flow/service.json` (read here first, so its other keys are kept), then the
 * caller starts the service (`onSaved(nextSpec)`). Without `canStart` the button only saves.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import * as vfsClient from '../../utils/vfsClient';
import { parseSpec } from '../../utils/serviceStackModel';
import BuilderPicker from './BuilderPicker';

export default function StartSetup({
  open, projectId, serviceName, info = null, docker = null, canStart = true, onClose, onSaved = null,
}) {
  // undefined while the file is read; null when there is none (or it is not JSON: the picker writes a new one).
  const [spec, setSpec] = useState(undefined);
  useEffect(() => {
    if (!open) { setSpec(undefined); return undefined; }
    let live = true;
    vfsClient.readFile(projectId, `${serviceName}/.flow/service.json`)
      .then((text) => { if (live) { setSpec(parseSpec(text)); } })
      .catch(() => { if (live) { setSpec(null); } });
    return () => { live = false; };
  }, [open, projectId, serviceName]);
  const short = serviceName.replace(/^services\//, '');
  return (
    <Modal destroyOnHidden footer={null} onCancel={onClose} open={open} title={`Set how ${short} starts`} width={640}>
      <div data-testid="apps-start-setup">
        <p className="apps-muted apps-setup-intro">
          Flow found no stack it starts by itself in this folder. Pick how it runs, or give the command that starts it.
        </p>
        {spec === undefined ? <p className="apps-muted">Loading…</p> : (
          <BuilderPicker
            docker={docker}
            info={info || { builders: [], recommended: null, option: null }}
            onSaved={(next) => { onClose(); onSaved?.(next); }}
            projectId={projectId}
            saveLabel={canStart ? 'Save and start' : 'Save'}
            serviceName={serviceName}
            setup
            spec={spec}
          />
        )}
      </div>
    </Modal>
  );
}

StartSetup.propTypes = {
  canStart: PropTypes.bool,
  docker: PropTypes.bool,
  // vfs-service's builders of this service (GET /runnables › builders["services/<name>"]).
  info: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  // (nextSpec) => after the choice was written: the caller starts the service.
  onSaved: PropTypes.func,
  open: PropTypes.bool.isRequired,
  projectId: PropTypes.string.isRequired,
  serviceName: PropTypes.string.isRequired,
};
